import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SUPPORT_EMAIL = "soporte@cadii.app";
const LAST_UPDATED = "19 de agosto de 2026";
const LEGAL_ENTITY = "Multigoods Inmuebles, S. de R.L. de C.V.";

const PRIVACY_SECTIONS = [
  { heading: "Responsable del tratamiento", body: `${LEGAL_ENTITY} ("CADii", "we", "us" or "our") operates the CADii platform and the website cadii.app. Todas las referencias en esta política a "nosotros" hacen referencia a ${LEGAL_ENTITY}.` },
  { heading: "1. Información que recopilamos", body: "Recopilamos los datos que nos proporcionas al crear tu cuenta (nombre, correo electrónico y, opcionalmente, teléfono y club), tu handicap y la información de tus rondas de golf (scores, apuestas, estadísticas y fotos/videos que decidas crear). Si inicias sesión con Apple o Google, recibimos un identificador y tu correo (que puede ser un correo de retransmisión privada de Apple)." },
  { heading: "2. Cómo usamos tus datos", body: "Usamos tu información para autenticarte, calcular tu handicap y resultados, gestionar competencias y rankings, y mejorar la app. No vendemos tus datos personales a terceros." },
  { heading: "3. Almacenamiento y seguridad", body: "Tus datos se almacenan de forma segura en nuestros servidores. Las contraseñas se guardan cifradas y la comunicación con la app se realiza mediante conexiones HTTPS." },
  { heading: "4. Tus derechos y eliminación de cuenta", body: "Puedes editar tu perfil en cualquier momento. Puedes eliminar tu cuenta de forma permanente desde Perfil → Eliminar cuenta; esto borra tu cuenta, tus rondas y tu información personal, y revoca las credenciales de Sign in with Apple asociadas." },
  { heading: "5. Contacto", body: `Si tienes preguntas sobre esta política o el manejo de tus datos, escríbenos a ${SUPPORT_EMAIL}.` },
];

const TERMS_SECTIONS = [
  { heading: "Aviso legal", body: `These Terms of Service govern the use of CADii, a platform operated by ${LEGAL_ENTITY}. Al utilizar CADii aceptas estar sujeto a estos términos frente a ${LEGAL_ENTITY}.` },
  { heading: "1. Aceptación", body: "Al usar CADii aceptas estos términos. Si no estás de acuerdo, no utilices la aplicación." },
  { heading: "2. Uso de la aplicación", body: "CADii es una herramienta para registrar rondas de golf, calcular handicap, gestionar apuestas amistosas entre jugadores y crear contenido. Eres responsable de la exactitud de la información que ingresas." },
  { heading: "3. Apuestas amistosas", body: "Las funciones de apuestas (Nassau, Skins, Match Play, etc.) son un marcador para juegos recreativos entre jugadores. CADii no procesa ni gestiona dinero real ni actúa como intermediario de pagos entre jugadores." },
  { heading: "4. Cuenta", body: "Eres responsable de mantener la confidencialidad de tu cuenta. Puedes eliminarla en cualquier momento desde tu perfil." },
  { heading: "5. Cambios y contacto", body: `Podemos actualizar estos términos. Para dudas, contáctanos en ${SUPPORT_EMAIL}.` },
];

function LegalLayout({ title, sections, testid, seoTitle, seoDescription }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid={testid}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-[#1B4D3E] font-semibold text-sm hover:underline" data-testid="legal-home-link">
          ← Volver
        </Link>
        <h1 className="text-3xl font-black text-[#102E32] mt-6">{title}</h1>
        <p className="text-sm text-[#64748B] mt-2">Última actualización: {LAST_UPDATED}</p>
        <div className="mt-8 space-y-6">
          {sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-lg font-bold text-[#102E32] mb-2">{s.heading}</h2>
              <p className="text-[#475569] leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-[#E5E7EB]" data-testid="legal-entity-block">
          <p className="text-xs text-[#64748B] leading-relaxed">
            CADii es operado por <strong className="text-[#102E32]">{LEGAL_ENTITY}</strong>.
            <br />
            CADii is operated by <strong className="text-[#102E32]">{LEGAL_ENTITY}</strong>.
          </p>
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#64748B]">
            <Link to="/privacy" className="hover:text-[#1B4D3E] hover:underline">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-[#1B4D3E] hover:underline">Terms of Service</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-[#1B4D3E] hover:underline">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalLayout
      title="Política de privacidad"
      sections={PRIVACY_SECTIONS}
      testid="privacy-page"
      seoTitle="Privacy Policy · CADii"
      seoDescription={`Privacy Policy for CADii, operated by ${LEGAL_ENTITY}.`}
    />
  );
}

export function Terms() {
  return (
    <LegalLayout
      title="Términos de uso"
      sections={TERMS_SECTIONS}
      testid="terms-page"
      seoTitle="Terms of Service · CADii"
      seoDescription={`Terms of Service for CADii, operated by ${LEGAL_ENTITY}.`}
    />
  );
}

export default Privacy;
